import { useState, useEffect, useRef } from 'react'
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap'
import client from '../api/client'
import ChatMessage from '../components/ChatMessage'
import ChatInput from '../components/ChatInput'

const STORAGE_KEY = 'admin_test_chat_messages'
const MODEL_KEY = 'admin_test_chat_model'

function TestChat() {
  const [models, setModels] = useState([])
  const [selectedModel, setSelectedModel] = useState('')
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(false)
  const [modelsLoading, setModelsLoading] = useState(true)
  const [error, setError] = useState('')
  const messagesEndRef = useRef(null)

  // Load models on mount
  useEffect(() => {
    fetchModels()
  }, [])

  // Load messages from sessionStorage on mount
  useEffect(() => {
    const savedMessages = sessionStorage.getItem(STORAGE_KEY)
    if (savedMessages) {
      try {
        setMessages(JSON.parse(savedMessages))
      } catch (e) {
        console.error('Failed to parse saved messages:', e)
      }
    }
    const savedModel = sessionStorage.getItem(MODEL_KEY)
    if (savedModel) {
      setSelectedModel(savedModel)
    }
  }, [])

  // Save messages to sessionStorage when they change
  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages))
    }
  }, [messages])

  // Save selected model to sessionStorage
  useEffect(() => {
    if (selectedModel) {
      sessionStorage.setItem(MODEL_KEY, selectedModel)
    }
  }, [selectedModel])

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const fetchModels = async () => {
    setModelsLoading(true)
    try {
      // Fetch all models (including inactive) for testing
      const response = await client.get('/admin/api/v1/models', {
        params: { limit: 500 }
      })
      const allModels = response.data.models || []
      setModels(allModels)
      // Set default model if not already set
      if (!selectedModel && allModels.length > 0) {
        setSelectedModel(allModels[0].code)
      }
    } catch (err) {
      setError('Erreur lors du chargement des modèles')
      console.error(err)
    } finally {
      setModelsLoading(false)
    }
  }

  const handleSend = async ({ text, image }) => {
    if (!selectedModel) {
      setError('Veuillez sélectionner un modèle')
      return
    }

    // Create user message
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: text,
      image: image,
      timestamp: new Date().toISOString(),
    }

    setMessages((prev) => [...prev, userMessage])
    setLoading(true)
    setError('')

    try {
      // Build messages array for API
      const apiMessages = messages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({
          role: m.role,
          content: m.content,
        }))

      // Add current message
      apiMessages.push({
        role: 'user',
        content: image ? [
          { type: 'text', text: text || 'Décrivez cette image' },
          { type: 'image_url', image_url: { url: image } }
        ] : text,
      })

      const response = await client.post('/api/v1/chat/completions', {
        model: selectedModel,
        messages: apiMessages,
        temperature: 0.7,
        max_tokens: 2000,
        stream: false,
      })

      const assistantContent = response.data.choices?.[0]?.message?.content || 'Pas de réponse'

      // Check if response contains generated media (image/video URLs)
      let generatedImage = null
      let generatedVideo = null

      // Check for image generation response
      if (response.data.data?.[0]?.url) {
        generatedImage = response.data.data[0].url
      }
      // Check for video URL in content
      if (assistantContent.includes('.mp4') || assistantContent.includes('video')) {
        const videoMatch = assistantContent.match(/https?:\/\/[^\s]+\.mp4/i)
        if (videoMatch) {
          generatedVideo = videoMatch[0]
        }
      }

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: assistantContent,
        generatedImage: generatedImage,
        generatedVideo: generatedVideo,
        timestamp: new Date().toISOString(),
      }

      setMessages((prev) => [...prev, assistantMessage])
    } catch (err) {
      console.error('Chat error:', err)
      const errorMessage = err.response?.data?.detail || 'Erreur lors de l\'envoi du message'
      setError(errorMessage)

      // Add error message to chat
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: 'assistant',
          content: `⚠️ Erreur: ${errorMessage}`,
          timestamp: new Date().toISOString(),
        },
      ])
    } finally {
      setLoading(false)
    }
  }

  const clearConversation = () => {
    setMessages([])
    sessionStorage.removeItem(STORAGE_KEY)
  }

  const getModelInfo = (code) => {
    return models.find(m => m.code === code)
  }

  const selectedModelInfo = getModelInfo(selectedModel)

  return (
    <Container fluid className="py-3">
      <Row className="mb-3">
        <Col>
          <h1 className="h3">Test de modèle</h1>
          <p className="text-muted">
            Testez n'importe quel modèle LLM avec support multimodal (texte et images)
          </p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Row>
        {/* Model selector sidebar */}
        <Col md={3}>
          <Card className="mb-3">
            <Card.Header>Sélection du modèle</Card.Header>
            <Card.Body>
              {modelsLoading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : (
                <>
                  <Form.Select
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    className="mb-3"
                  >
                    <option value="">Sélectionner un modèle...</option>
                    {models.map((model) => (
                      <option key={model.id} value={model.code}>
                        {model.name || model.code}
                        {!model.active && ' (inactif)'}
                      </option>
                    ))}
                  </Form.Select>

                  {selectedModelInfo && (
                    <div className="small text-muted">
                      <strong>Fournisseur:</strong> {selectedModelInfo.provider}
                      <br />
                      <strong>Type:</strong> {selectedModelInfo.type || 'LLM'}
                      <br />
                      {selectedModelInfo.pricing_input && (
                        <>
                          <strong>Coût entrée:</strong> ${selectedModelInfo.pricing_input}/1K
                          <br />
                        </>
                      )}
                      {selectedModelInfo.pricing_output && (
                        <>
                          <strong>Coût sortie:</strong> ${selectedModelInfo.pricing_output}/1K
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>Actions</Card.Header>
            <Card.Body>
              <Button
                variant="outline-danger"
                className="w-100"
                onClick={clearConversation}
                disabled={messages.length === 0}
              >
                Effacer la conversation
              </Button>
              <div className="text-muted small mt-2">
                {messages.length} message{messages.length !== 1 ? 's' : ''} dans la session
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Chat area */}
        <Col md={9}>
          <Card className="h-100" style={{ minHeight: '500px' }}>
            <Card.Header className="bg-white">
              <div className="d-flex justify-content-between align-items-center">
                <span>Conversation</span>
                {selectedModelInfo && (
                  <span className={`badge ${selectedModelInfo.active ? 'bg-success' : 'bg-secondary'}`}>
                    {selectedModelInfo.name || selectedModelInfo.code}
                  </span>
                )}
              </div>
            </Card.Header>
            <Card.Body
              className="overflow-auto"
              style={{ maxHeight: '400px', minHeight: '300px' }}
            >
              {messages.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <p>Aucun message</p>
                  <p className="small">
                    Sélectionnez un modèle et commencez à taper pour tester
                  </p>
                </div>
              ) : (
                messages.map((message) => (
                  <ChatMessage key={message.id} message={message} />
                ))
              )}
              <div ref={messagesEndRef} />
            </Card.Body>
            <Card.Footer className="bg-white">
              <ChatInput onSend={handleSend} disabled={loading || !selectedModel} />
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default TestChat
